import { useState } from 'react';
import type {
  PortalChannelBlock,
  PortalGrouping,
  PortalMetric,
  PortalReportLabels,
} from '../../../../types/portalReport';
import { PORTAL_METRICS } from '../../../../types/portalReport';
import { metricLabel } from '../../../../lib/portalReport/config';
import { buildPortalDeltas, portalMetricTone } from '../../../../lib/portalReport/deltas';
import { PORTAL_COMPARISON_MODES } from '../../../../lib/portalReport/comparison';
import { deltaColorClass } from '../../../reporting/heroCards';
import { formatReportingAmount } from '../../../../lib/reportingMoney';
import { ReportChart } from './ReportChart';
import { PeriodTrend } from './PeriodTrend';
import type { PortalDelta } from '../../../../types/portalReport';

// CLIENT PORTAL Phase 1 — one channel's block. Used THREE TIMES on the Overview
// (Combined, Google, Meta) and once at the top of each channel page, so the
// three read as parallel rather than as three different reports.
//
// ⚠️ MONEY IS NATIVE, ALWAYS. Every figure here is rendered with its own
// currency marker and never converted; there is deliberately no display-currency
// selector, because that machinery answers an agency question ("what did the
// whole portfolio do in AUD") and a client is asking about their own business.
// A mixed-currency scope shows both totals side by side rather than a blend.

const CHANNEL_TITLES: Record<PortalChannelBlock['channel'], string> = {
  combined: 'All advertising',
  google: 'Google Ads',
  meta: 'Meta (Facebook & Instagram)',
};

function formatLeads(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 1 });
}

/** How fresh this channel's data is — stated, because silence implies live. */
function freshnessNote(block: PortalChannelBlock): string | null {
  if (!block.asOf) return null;
  const label = new Date(`${block.asOf}T00:00:00Z`).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
  if (block.channel === 'google') {
    // ⚠️ Google's transfer lands a day behind. Naming the date is what stops a
    // client reading today's absence as a collapse.
    return `Google data is complete to ${label}.`;
  }
  if (block.channel === 'combined') return `Includes data up to ${label}.`;
  return `Data is complete to ${label}.`;
}

export function ChannelBlock({
  block,
  labels,
  grouping,
  onGroupingChange,
}: {
  block: PortalChannelBlock;
  labels: PortalReportLabels;
  grouping: PortalGrouping;
  onGroupingChange: (g: PortalGrouping) => void;
}) {
  const [metric, setMetric] = useState<PortalMetric>('leads');
  const currency = block.totals[0]?.currency ?? null;
  const mixedCurrency = block.totals.length > 1;
  const freshness = freshnessNote(block);

  const totalLeads = block.totals.reduce((sum, t) => sum + t.leads, 0);
  const hasAnything = block.totals.length > 0;

  // ⚠️ The percentage is computed HERE with the staff dashboards' own
  // `computeDelta`; the server supplied the comparison window and its totals.
  // See src/lib/portalReport/comparison.ts for why the two halves are split.
  const deltas = block.comparison
    ? buildPortalDeltas(
        block.totals,
        block.comparison.totals,
        block.comparison.unavailableReason,
      )
    : null;
  const comparisonLabel = block.comparison
    ? (PORTAL_COMPARISON_MODES.find((m) => m.value === block.comparison?.mode)?.label ?? null)
    : null;

  return (
    <section className="rounded-lg border border-border bg-card p-5">
      <h2 className="text-lg font-semibold text-foreground">
        {CHANNEL_TITLES[block.channel]}
      </h2>

      {!hasAnything ? (
        // ⚠️ An honest empty state. "No spend in this period" is a fact worth
        // stating plainly; a blank block reads as something broken.
        <p className="mt-4 text-sm text-muted-foreground">
          No {CHANNEL_TITLES[block.channel]} activity in this period.
        </p>
      ) : (
        <>
          {/* ⚠️ Round 2, item 3: TOTAL SPEND is a headline figure alongside the
              other two, in every block and on both channel pages — not a
              footnote under them. */}
          <div data-testid="headline-stats" className="mt-4 grid gap-4 sm:grid-cols-3">
            <Stat
              label={`Total ${labels.leads.toLowerCase()}`}
              value={formatLeads(totalLeads)}
              delta={deltas?.leads ?? null}
              tone={portalMetricTone('leads')}
              comparisonLabel={comparisonLabel}
              unavailable={deltas?.unavailableReason ?? null}
            />
            <Stat
              label={labels.costPerLead}
              value={
                block.totals.length === 1
                  ? block.totals[0]?.costPerLead != null
                    ? formatReportingAmount(
                        block.totals[0].costPerLead,
                        block.totals[0].currency,
                      )
                    : '—'
                  : // ⚠️ Per currency, never blended into one CPL.
                    block.totals
                      .map((t) =>
                        t.costPerLead != null
                          ? formatReportingAmount(t.costPerLead, t.currency)
                          : '—',
                      )
                      .join(' · ')
              }
              hint={
                block.totals.every((t) => t.costPerLead == null)
                  ? `No ${labels.leads.toLowerCase()} in this period, so there is no ${labels.costPerLead.toLowerCase()} to show.`
                  : undefined
              }
              delta={deltas?.costPerLead ?? null}
              tone={portalMetricTone('costPerLead')}
              comparisonLabel={comparisonLabel}
              unavailable={deltas?.unavailableReason ?? null}
            />
            <Stat
              label={`Total ${labels.spend.toLowerCase()}`}
              value={
                block.totals.length === 0
                  ? '—'
                  : // ⚠️ Per currency, never blended.
                    block.totals
                      .map((t) => formatReportingAmount(t.spend, t.currency))
                      .join(' · ')
              }
              delta={deltas?.spend ?? null}
              tone={portalMetricTone('spend')}
              comparisonLabel={comparisonLabel}
              unavailable={deltas?.unavailableReason ?? null}
            />
          </div>

          <div className="mt-6">
            <PeriodTrend
              series={block.periodSeries}
              labels={labels}
              currency={currency}
              coverage={block.coverage}
              grouping={grouping}
              onGroupingChange={onGroupingChange}
            />
          </div>

          <div className="mt-8">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-medium text-foreground">Last 13 months</h3>
              <MetricSwitch metric={metric} onChange={setMetric} labels={labels} />
            </div>
            <ReportChart
              series={block.thirteenMonthSeries}
              metric={metric}
              metricLabel={metricLabel(metric, labels)}
              leadsLabel={labels.leads}
              currency={currency}
              coverage={block.coverage}
              shape="bar"
            />
          </div>

          {mixedCurrency && (
            // ⚠️ Stated rather than silently resolved. The charts plot one
            // currency because a single line cannot honestly carry two; the
            // totals above remain complete.
            <p className="mt-3 text-xs text-muted-foreground">
              This report covers more than one currency. The totals show each
              separately; the charts show {currency} only.
            </p>
          )}
        </>
      )}

      {freshness && <p className="mt-4 text-xs text-muted-foreground">{freshness}</p>}
    </section>
  );
}

function Stat({
  label,
  value,
  hint,
  delta,
  tone,
  comparisonLabel,
  unavailable,
}: {
  label: string;
  value: string;
  hint?: string;
  delta?: PortalDelta | null;
  tone?: 'good-up' | 'good-down' | 'neutral';
  comparisonLabel?: string | null;
  unavailable?: string | null;
}) {
  return (
    <div className="rounded-md bg-background px-4 py-3">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-foreground">{value}</div>
      {delta && comparisonLabel && (
        <div className="mt-1 flex items-baseline gap-1.5 text-xs">
          {/*
            ⚠️ TONE FOLLOWS MEANING. `deltaColorClass` is the staff dashboards'
            own, so a FALLING cost per lead is green while a falling lead count
            is red — the direction alone would colour both the same way.
          */}
          <span className={`font-medium ${deltaColorClass(tone ?? 'neutral', delta.direction)}`}>
            {delta.direction === 'flat'
              ? 'No change'
              : `${delta.pct > 0 ? '+' : ''}${delta.pct}%`}
          </span>
          <span className="text-muted-foreground">{comparisonLabel.toLowerCase()}</span>
        </div>
      )}
      {!delta && unavailable && (
        // ⚠️ The REASON, never a fabricated −100%. A client whose reporting
        // starts inside the comparison window is told that, not that their
        // performance collapsed.
        <div className="mt-1 text-xs text-muted-foreground">{unavailable}</div>
      )}
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function MetricSwitch({
  metric,
  onChange,
  labels,
}: {
  metric: PortalMetric;
  onChange: (m: PortalMetric) => void;
  labels: PortalReportLabels;
}) {
  return (
    <div className="inline-flex rounded-md border border-border p-0.5" role="group">
      {PORTAL_METRICS.map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => onChange(m)}
          aria-pressed={metric === m}
          className={
            metric === m
              ? 'rounded px-2.5 py-1 text-xs font-medium'
              : 'rounded px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted'
          }
          // ⚠️ ONE OF THE FOUR BRAND SURFACES. The SELECTED chip takes the brand
          // accent; unselected ones stay kyck, and an unthemed portal falls
          // through to the kyck default (slate-900 on white) unchanged.
          //
          // ⚠️ The foreground is DERIVED (readableOn), never hardcoded white:
          // Fitstop's orange with white text is ~3.4:1, below AA at this size.
          style={
            metric === m
              ? {
                  backgroundColor: 'var(--portal-accent)',
                  color: 'var(--portal-accent-foreground)',
                }
              : undefined
          }
        >
          {metricLabel(m, labels)}
        </button>
      ))}
    </div>
  );
}

function GroupingSwitch({
  grouping,
  onChange,
}: {
  grouping: PortalGrouping;
  onChange: (g: PortalGrouping) => void;
}) {
  return (
    <div className="inline-flex rounded-md border border-border p-0.5" role="group">
      {(['daily', 'monthly'] as const).map((g) => (
        <button
          key={g}
          type="button"
          onClick={() => onChange(g)}
          aria-pressed={grouping === g}
          className={
            grouping === g
              ? 'rounded px-2.5 py-1 text-xs font-medium'
              : 'rounded px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted'
          }
          style={
            grouping === g
              ? {
                  backgroundColor: 'var(--portal-accent)',
                  color: 'var(--portal-accent-foreground)',
                }
              : undefined
          }
        >
          {g === 'daily' ? 'Daily' : 'Monthly'}
        </button>
      ))}
    </div>
  );
}
